import { type ReactNode, useEffect, useId, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PERMISSIONS, type OrderResponse, type ShipmentResponse } from '@aps/contracts';
import {
  Button,
  EmptyState,
  Skeleton,
  formatDate,
  formatDateTime,
  formatMoney,
  useToast,
} from '@aps/ui';
import { ApiError, api } from '../lib/api.js';
import { messages } from '../i18n/messages.js';
import { useSession } from '../app/session-context.js';
import { can } from '../lib/session.js';
import { QueueError } from './WorkQueueScreen.js';
import { CourierAssign } from './CourierAssign.js';
import { StatusChanger } from './StatusChanger.js';
import { PaymentsPanel } from './PaymentsPanel.js';

interface OrderScreenProps {
  orderId: string;
  onBack: () => void;
}

/**
 * Buyurtma kartochkasi.
 *
 * Operatorning ish joyi: mijoz, tovarlar, jo'natmalar, to'lovlar, status
 * o'zgartirish va tarix. Statusni o'zgartirish tugmalari
 * `status_transitions` jadvalidan quriladi — bu yerda graf yozilmaydi.
 */
export function OrderScreen({ orderId, onBack }: OrderScreenProps): ReactNode {
  const { locale, me } = useSession();
  const t = messages(locale);

  const order = useQuery({
    queryKey: ['order', orderId],
    queryFn: () => api.order(orderId),
  });

  const header = (
    <header className="adm-page__header">
      <h1 className="adm-page__title">
        {t.order} {order.data?.number ?? ''}
      </h1>
      <Button variant="ghost" size="sm" onClick={onBack}>
        {t.back}
      </Button>
    </header>
  );

  if (order.isPending) {
    return (
      <section className="adm-page">
        {header}
        <Skeleton height="320px" label={t.loading} />
      </section>
    );
  }

  if (order.isError) {
    return (
      <section className="adm-page">
        {header}
        <QueueError
          message={order.error instanceof ApiError ? order.error.text(locale) : t.errorTitle}
          retryLabel={t.retry}
          onRetry={() => void order.refetch()}
        />
      </section>
    );
  }

  const data = order.data;

  return (
    <section className="adm-page">
      {header}

      <div className="adm-columns">
        <div className="adm-stack">
          <Panel title={t.orderCustomer}>
            <Field label={t.orderCustomer} value={data.customerName} />
            <Field label="Telefon" value={data.customerPhone} />
            <Field
              label={t.orderDelivery}
              value={data.deliveryMethod === 'COURIER' ? t.deliveryCourier : t.deliveryPickup}
              strong={data.deliveryMethod === 'COURIER'}
            />
            <Field label={t.orderRegion} value={data.regionNameUz ?? '—'} />
            <Field label={t.orderAddress} value={data.addressLine ?? '—'} />
            <Field label={t.orderSource} value={data.source} />
            {data.comment !== null && <Field label={t.orderComment} value={data.comment} />}
          </Panel>

          <Panel title={t.orderOperatorNote}>
            <OperatorNote
              orderId={orderId}
              value={data.operatorNote}
              canEdit={can(me, PERMISSIONS.ORDER_EDIT_NOTE)}
            />
          </Panel>

          <Panel title={t.orderPayments}>
            <PaymentsPanel order={data} />
          </Panel>
        </div>

        <div className="adm-stack">
          {data.shipments.map((shipment) => (
            <ShipmentPanel key={shipment.id} shipment={shipment} orderId={orderId} order={data} />
          ))}
        </div>
      </div>
    </section>
  );
}

/* ------------------------------ bo'laklar ------------------------------ */

function Panel({ title, children }: { title: string; children: ReactNode }): ReactNode {
  return (
    <section className="adm-panel">
      <h2 className="adm-panel__title">{title}</h2>
      <div className="adm-panel__body">{children}</div>
    </section>
  );
}

function Field({
  label,
  value,
  strong = false,
}: {
  label: string;
  value: string;
  strong?: boolean;
}): ReactNode {
  return (
    <div className="adm-kv">
      <span className="adm-kv__key">{label}</span>
      <span className={strong ? 'adm-kv__value adm-kv__value--strong' : 'adm-kv__value'}>
        {value}
      </span>
    </div>
  );
}

function ShipmentPanel({
  shipment,
  orderId,
  order,
}: {
  shipment: ShipmentResponse;
  orderId: string;
  order: OrderResponse;
}): ReactNode {
  const { locale } = useSession();
  const t = messages(locale);
  const statusLabel =
    locale === 'RU' ? (shipment.statusLabelRu ?? shipment.statusLabelUz) : shipment.statusLabelUz;

  return (
    <Panel title={`${t.orderShipments} · ${shipment.type} · ${statusLabel}`}>
      <ShopAssign shipment={shipment} orderId={orderId} />
      <Field
        label={t.shipmentEta}
        value={shipment.etaTo === null ? t.etaUnset : formatDate(shipment.etaTo, locale)}
      />
      {shipment.etaShiftCount > 0 && (
        // Ko'p siljish — ta'minotchi muammosi signali (docs/02-MAHSULOT.md).
        <Field label={t.shipmentEta} value={t.shipmentEtaShifted(shipment.etaShiftCount)} />
      )}
      {shipment.trackingNo !== null && (
        <Field label={t.shipmentTracking} value={shipment.trackingNo} />
      )}

      <h3 className="adm-panel__subtitle">{t.orderItems}</h3>
      <ul className="adm-items">
        {shipment.items.map((item) => (
          <li className="adm-items__row" key={item.id}>
            <span>
              {item.productName}
              <br />
              <span className="aps-muted">
                {item.brandName} · {item.productSku}
              </span>
            </span>
            <span className="adm-items__qty">×{item.qty}</span>
            <span className="adm-items__sum">{formatMoney(item.lineTotal, locale)}</span>
          </li>
        ))}
      </ul>

      <CourierAssign shipment={shipment} orderId={orderId} />

      <h3 className="adm-panel__subtitle">{t.statusChange}</h3>
      <StatusChanger shipmentId={shipment.id} orderId={orderId} locale={locale} />

      <h3 className="adm-panel__subtitle">{t.orderHistory}</h3>
      <ShipmentHistory shipmentId={shipment.id} orderNumber={order.number} />
    </Panel>
  );
}

/**
 * Jo'natmaga do'kon biriktirish.
 *
 * Bu boshqaruv UMUMAN YO'Q edi: `api.assignShop` e'lon qilingan,
 * server endpointi bor, ish navbatida "Tanlanmagan" qizil bilan
 * ko'rsatilardi — lekin operator uni tuzata olmasdi. Status esa
 * do'konsiz OLDINGA KETMAYDI ("Avval jo'natma uchun do'kon
 * tanlang"), ya'ni buyurtma UIda tiqilib qolardi.
 *
 * Brauzer E2E shuni topdi: statusni o'zgartirish testi ekranda
 * xato ko'rsatib to'xtadi.
 */
function ShopAssign({
  shipment,
  orderId,
}: {
  shipment: ShipmentResponse;
  orderId: string;
}): ReactNode {
  const { me, locale } = useSession();
  const t = messages(locale);
  const client = useQueryClient();
  const toast = useToast();
  const selectId = useId();
  const [shopId, setShopId] = useState('');

  const canAssign = can(me, PERMISSIONS.ORDER_CHANGE_STATUS);

  const shops = useQuery({
    queryKey: ['shops'],
    queryFn: api.shops,
    // Ruxsati yo'q xodimga ro'yxat ham kerak emas.
    enabled: canAssign,
  });

  const assign = useMutation({
    mutationFn: () => api.assignShop(shipment.id, shopId),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['order', orderId] });
      void client.invalidateQueries({ queryKey: ['work-queue'] });
      toast.show(t.toastShopAssigned, 'success');
    },
    onError: (cause: unknown) => {
      toast.show(cause instanceof ApiError ? cause.text(locale) : t.errorTitle, 'danger');
    },
  });

  // Biriktirilgan yoki ruxsati yo'q bo'lsa — oddiy qator.
  if (shipment.shopNameUz !== null || !canAssign) {
    return <Field label={t.shipmentShop} value={shipment.shopNameUz ?? t.shopUnset} />;
  }

  return (
    <div className="adm-shop-assign">
      <label className="aps-field__label" htmlFor={selectId}>
        {t.shipmentShop}
      </label>
      <select
        id={selectId}
        className="aps-field__input"
        value={shopId}
        onChange={(event) => {
          setShopId(event.target.value);
        }}
      >
        <option value="">{t.shipmentSelectShop}</option>
        {(shops.data ?? []).map((shop) => (
          <option key={shop.id} value={shop.id}>
            {locale === 'RU' ? (shop.nameRu ?? shop.nameUz) : shop.nameUz}
          </option>
        ))}
      </select>
      <Button
        size="sm"
        disabled={shopId === '' || assign.isPending}
        onClick={() => {
          assign.mutate();
        }}
      >
        {t.shipmentAssignShop}
      </Button>
    </div>
  );
}

function ShipmentHistory({ shipmentId }: { shipmentId: string; orderNumber: string }): ReactNode {
  const { locale } = useSession();
  const t = messages(locale);

  const detail = useQuery({
    queryKey: ['shipment', shipmentId],
    queryFn: () => api.shipment(shipmentId),
  });

  if (detail.isPending) return <Skeleton height="80px" label={t.loading} />;
  if (detail.isError) return <p className="aps-muted">{t.errorTitle}</p>;
  if (detail.data.history.length === 0) {
    return <EmptyState title={t.historyEmpty} />;
  }

  return (
    <ol className="adm-history">
      {[...detail.data.history].reverse().map((entry) => (
        <li className="adm-history__row" key={entry.id}>
          <span className="adm-history__when">{formatDateTime(entry.createdAt, locale)}</span>
          <span className="adm-history__what">
            {entry.fromLabelUz ?? '—'} → {entry.toLabelUz}
          </span>
          <span className="aps-muted">
            {/* `actorId` null — o'zgarishni TIZIM qilgan (partiya, navbat). */}
            {entry.actorName ?? t.historySystem}
            {entry.comment === null ? '' : ` · ${entry.comment}`}
          </span>
        </li>
      ))}
    </ol>
  );
}

function OperatorNote({
  orderId,
  value,
  canEdit,
}: {
  orderId: string;
  value: string | null;
  canEdit: boolean;
}): ReactNode {
  const { locale } = useSession();
  const t = messages(locale);
  const client = useQueryClient();
  const toast = useToast();

  const [draft, setDraft] = useState(value ?? '');
  const [error, setError] = useState<string | null>(null);

  // Boshqa joydan yangilansa (yoki boshqa buyurtma ochilsa) matn ergashsin.
  useEffect(() => {
    setDraft(value ?? '');
  }, [value, orderId]);

  const save = useMutation({
    mutationFn: () =>
      api.updateOperatorNote(orderId, { operatorNote: draft.trim() === '' ? null : draft.trim() }),
    onSuccess: () => {
      setError(null);
      void client.invalidateQueries({ queryKey: ['order', orderId] });
      // Kichik "Saqlandi" yozuvi o'rniga: operator natijani aniq ko'rsin.
      toast.show(t.toastNoteSaved, 'success');
    },
    onError: (cause: unknown) => {
      setError(cause instanceof ApiError ? cause.text(locale) : t.errorTitle);
    },
  });

  if (!canEdit) {
    return <p className={value === null ? 'aps-muted' : ''}>{value ?? t.orderNoteEmpty}</p>;
  }

  return (
    <div className="adm-note">
      <p className="aps-field__hint">{t.orderOperatorNoteHint}</p>
      <textarea
        className="aps-field__input adm-note__input"
        rows={3}
        maxLength={1000}
        value={draft}
        onChange={(event) => {
          setDraft(event.target.value);
        }}
      />
      {error !== null && (
        <p className="aps-error" role="alert">
          {error}
        </p>
      )}
      <div className="adm-note__actions">
        <Button
          size="sm"
          variant="secondary"
          disabled={save.isPending || draft === (value ?? '')}
          onClick={() => {
            save.mutate();
          }}
        >
          {t.orderNoteSave}
        </Button>
      </div>
    </div>
  );
}
